import {
  BarChart3,
  Book,
  BookOpen,
  ChevronRight,
  Clock,
  Eye,
  FolderTree,
  GraduationCap,
  Home,
  MessageSquare,
  Star,
  TrendingUp,
  Upload,
  Users,
  Video,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { ManualSectionMeta } from '@/components/manual/ManualBlocks'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useTenantPath } from '@/lib/tenant'
import { TrainingManualContent, trainingManualSections } from './manual/TrainingManual'

const courseSections: ManualSectionMeta[] = [
  { id: 'intro', title: 'はじめに', icon: Book },
  { id: 'course-lesson', title: 'コースとレッスンの違い', icon: FolderTree },
  { id: 'course-management', title: 'コース管理', icon: BookOpen },
  { id: 'lesson-management', title: 'レッスン管理', icon: Video },
  { id: 'video-upload', title: '動画アップロード', icon: Upload },
  { id: 'analytics', title: 'レッスン分析機能', icon: BarChart3 },
  { id: 'student-management', title: '受講者管理', icon: Users },
  {
    id: 'qa-management',
    title: '質問・フィードバック管理',
    icon: MessageSquare,
  },
  { id: 'best-practices', title: 'ベストプラクティス', icon: Star },
]

const sectionGroups: { label: string; sections: ManualSectionMeta[] }[] = [
  { label: 'コース・レッスン', sections: courseSections },
  { label: '研修管理', sections: trainingManualSections },
]

export default function TeacherManualPage() {
  const [activeSection, setActiveSection] = useState('intro')
  const tp = useTenantPath()

  const scrollToSection = (id: string) => {
    setActiveSection(id)
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-xl font-bold text-foreground">講師・管理者向け操作マニュアル</h1>
            <p className="text-xs text-muted-foreground">コース・レッスン作成／研修管理ガイド</p>
          </div>
        </div>
        <Link to={tp('/teacher')}>
          <Button variant="outline" size="sm" className="gap-2">
            <Home className="h-4 w-4" />
            講師ダッシュボード
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6 p-4">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <Book className="h-5 w-5 text-indigo-600" />
              目次
            </h2>
            <ScrollArea className="h-[calc(100vh-200px)]">
              <nav className="space-y-4">
                {sectionGroups.map((group) => (
                  <div key={group.label} className="space-y-1">
                    <p className="px-3 text-xs font-semibold text-muted-foreground">
                      {group.label}
                    </p>
                    {group.sections.map((section) => {
                      const Icon = section.icon
                      const isActive = activeSection === section.id
                      return (
                        <button
                          type="button"
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 text-left">{section.title}</span>
                          {isActive && <ChevronRight className="h-4 w-4" />}
                        </button>
                      )
                    })}
                  </div>
                ))}
              </nav>
            </ScrollArea>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card className="p-8">
            <div className="max-w-3xl space-y-12">
              {/* はじめに */}
              <section id="intro" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <Book className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">はじめに</h2>
                </div>
                <div className="space-y-4">
                  <p className="leading-relaxed text-muted-foreground">
                    このマニュアルは、JB-LMSで教材（コース・レッスン）を作成・管理するためのガイドと、集合研修の申込・出欠・スキルチェックを管理する研修管理のガイドです。
                  </p>
                  <div className="rounded border-l-4 border-indigo-400 bg-indigo-50 p-4">
                    <p className="text-sm text-indigo-900">
                      <strong>対象読者：</strong>{' '}
                      講師権限を持つユーザー（コース・レッスンの作成、研修の運営）と、管理者権限を持つ人事担当者（組織マスター・研修設定・スキルチェックの管理）。
                    </p>
                  </div>
                </div>
              </section>

              <Separator />

              {/* コースとレッスンの違い */}
              <section id="course-lesson" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <FolderTree className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">コースとレッスンの違い</h2>
                </div>

                <div className="space-y-6">
                  <p className="text-muted-foreground">
                    JB-LMSでは、
                    <strong>コース</strong>と<strong>レッスン</strong>
                    という2つの重要な概念があります。この違いを理解することが効果的な教材作成の第一歩です。
                  </p>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* コース */}
                    <div className="rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-6">
                      <div className="mb-3 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-foreground">コース（Course）</h3>
                      </div>
                      <p className="mb-3 text-sm text-muted-foreground">
                        学習内容の大きな枠組み・カリキュラム全体
                      </p>
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground">特徴：</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <span>複数のレッスンをまとめたもの</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <span>タイトル、説明、カテゴリー、レベルを設定</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <span>受講者はコース単位で登録</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <span>コース全体の進捗が追跡される</span>
                          </li>
                        </ul>
                      </div>
                      <div className="mt-4 rounded bg-blue-100 p-3 text-xs text-blue-900">
                        <strong>例：</strong> 「AWS入門」「機械学習基礎」「セキュリティ応用」
                      </div>
                    </div>

                    {/* レッスン */}
                    <div className="rounded-lg border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 p-6">
                      <div className="mb-3 flex items-center gap-2">
                        <Video className="h-5 w-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-foreground">
                          レッスン（Lesson）
                        </h3>
                      </div>
                      <p className="mb-3 text-sm text-muted-foreground">
                        個別の学習単位・具体的な学習コンテンツ
                      </p>
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-foreground">特徴：</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <span>動画や外部リンクなどの実際のコンテンツ</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <span>タグで分類・整理される</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <span>複数のコースで共有可能</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <span>視聴時間や完了状況が個別に記録される</span>
                          </li>
                        </ul>
                      </div>
                      <div className="mt-4 rounded bg-purple-100 p-3 text-xs text-purple-900">
                        <strong>例：</strong> 「EC2の基本操作」「S3バケットの作成」
                      </div>
                    </div>
                  </div>

                  {/* 関係性の図 */}
                  <div className="rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6">
                    <h3 className="mb-4 text-lg font-semibold text-foreground">構造の関係性</h3>
                    <div className="space-y-3">
                      <div className="rounded-lg bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-32 shrink-0 font-semibold text-blue-700">コース</div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1 text-muted-foreground">「AWS入門コース」</div>
                        </div>
                      </div>
                      <div className="ml-8 space-y-2">
                        <div className="rounded-lg border-l-4 border-purple-400 bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-24 shrink-0 text-sm font-semibold text-purple-700">
                              レッスン 1
                            </div>
                            <div className="flex-1 text-sm text-muted-foreground">
                              「AWSとは何か」（動画）
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border-l-4 border-purple-400 bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-24 shrink-0 text-sm font-semibold text-purple-700">
                              レッスン 2
                            </div>
                            <div className="flex-1 text-sm text-muted-foreground">
                              「EC2インスタンスの作成」（動画）
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border-l-4 border-purple-400 bg-white p-3 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-24 shrink-0 text-sm font-semibold text-purple-700">
                              レッスン 3
                            </div>
                            <div className="flex-1 text-sm text-muted-foreground">
                              「S3の基本操作」（動画）
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-muted-foreground">
                      <strong>ポイント：</strong>{' '}
                      同じレッスンを複数のコースで使い回すことができます。例えば「EC2インスタンスの作成」レッスンは、「AWS入門」と「インフラ基礎」の両方で使用可能です。
                    </div>
                  </div>

                  {/* ワークフロー */}
                  <div className="rounded border-l-4 border-green-400 bg-green-50 p-4">
                    <h4 className="mb-2 font-semibold text-green-900">作成の推奨ワークフロー</h4>
                    <ol className="space-y-2 text-sm text-green-900">
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-200 text-xs font-semibold text-green-800">
                          1
                        </span>
                        <span>
                          <strong>レッスンを先に作成：</strong>{' '}
                          個別の動画コンテンツやレッスンを作成し、タグで分類
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-200 text-xs font-semibold text-green-800">
                          2
                        </span>
                        <span>
                          <strong>コースを作成：</strong>{' '}
                          コースの基本情報（タイトル、説明、カテゴリー）を設定
                        </span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-200 text-xs font-semibold text-green-800">
                          3
                        </span>
                        <span>
                          <strong>レッスンを紐付け：</strong>{' '}
                          タグを使って関連するレッスンをコースに追加
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              <Separator />

              {/* コース管理 */}
              <section id="course-management" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <BookOpen className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">コース管理</h2>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">コースの作成</h3>
                    <ol className="space-y-3 text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          1
                        </span>
                        <div>
                          <p className="font-medium">
                            講師ダッシュボードから「新規コース作成」をクリック
                          </p>
                          <p className="text-sm text-muted-foreground">
                            または「コース管理」→「新しいコースを作成」
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          2
                        </span>
                        <div>
                          <p className="font-medium">基本情報を入力</p>
                          <ul className="ml-4 mt-1 space-y-1 text-sm text-muted-foreground">
                            <li>
                              ・ <strong>タイトル：</strong> わかりやすく具体的に（例: 「AWS入門 -
                              クラウドの基礎を学ぶ」）
                            </li>
                            <li>
                              ・ <strong>説明：</strong> コースの目的、対象者、学習内容を明記
                            </li>
                            <li>
                              ・ <strong>カテゴリー：</strong>{' '}
                              クラウド、AI、セキュリティ、ITSPM、SIPMから選択
                            </li>
                            <li>
                              ・ <strong>レベル：</strong> 初級、中級、上級を設定
                            </li>
                            <li>
                              ・ <strong>サムネイル画像：</strong>{' '}
                              コースを視覚的に表現する画像（任意）
                            </li>
                          </ul>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          3
                        </span>
                        <span>「作成」ボタンをクリックして保存</span>
                      </li>
                    </ol>
                  </div>

                  <div className="rounded-lg border border-purple-100 bg-purple-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">
                      コースへのレッスン追加（タグ機能）
                    </h3>
                    <p className="mb-4 text-muted-foreground">
                      コースには直接レッスンを追加するのではなく、
                      <strong>タグ</strong>
                      を使ってレッスンを関連付けます。
                    </p>
                    <div className="space-y-3 rounded border bg-white p-4">
                      <div>
                        <h4 className="mb-2 text-sm font-semibold text-foreground">
                          タグの仕組み：
                        </h4>
                        <ol className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                              1
                            </span>
                            <span>各レッスンに1つまたは複数のタグを設定</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                              2
                            </span>
                            <span>コースにも同じタグを設定</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                              3
                            </span>
                            <span>タグが一致するレッスンが自動的にコースに表示される</span>
                          </li>
                        </ol>
                      </div>
                      <div className="rounded bg-purple-50 p-3">
                        <p className="text-xs text-purple-900">
                          <strong>例：</strong>{' '}
                          「AWS」タグを持つコースには、「AWS」タグが付いたすべてのレッスンが含まれます。
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-amber-100 bg-amber-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">
                      コースの公開/非公開
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <span>
                          <strong>非公開：</strong>{' '}
                          受講者には表示されません。準備中のコースはこの状態で作業
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <span>
                          <strong>公開：</strong>{' '}
                          コース一覧に表示され、受講者が登録できるようになります
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <Separator />

              {/* レッスン管理 */}
              <section id="lesson-management" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <Video className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">レッスン管理</h2>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg border border-cyan-100 bg-cyan-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">レッスンの作成</h3>
                    <ol className="space-y-3 text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-semibold text-cyan-600">
                          1
                        </span>
                        <span>「新規レッスン作成」をクリック</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-semibold text-cyan-600">
                          2
                        </span>
                        <div>
                          <p className="font-medium">レッスンタイプを選択</p>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div className="rounded border bg-white p-3 text-sm">
                              <strong>動画（Video）：</strong> 動画ファイルをアップロード
                            </div>
                            <div className="rounded border bg-white p-3 text-sm">
                              <strong>外部研修（External）：</strong> 外部サイトへのリンク
                            </div>
                          </div>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-semibold text-cyan-600">
                          3
                        </span>
                        <div>
                          <p className="font-medium">基本情報を入力</p>
                          <ul className="ml-4 mt-1 space-y-1 text-sm text-muted-foreground">
                            <li>・ タイトル</li>
                            <li>・ 説明（学習目標や内容を記載）</li>
                            <li>・ レベル（ENTRY/BASIC/ADVANCED/EXPERT）</li>
                            <li>・ タグ（複数設定可能）</li>
                          </ul>
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="rounded-lg border border-green-100 bg-green-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">タグの活用方法</h3>
                    <p className="mb-3 text-muted-foreground">
                      タグは、レッスンを整理しコースと紐付けるための重要な機能です。
                    </p>
                    <div className="space-y-3">
                      <div className="rounded border bg-white p-4">
                        <h4 className="mb-2 text-sm font-semibold text-foreground">
                          効果的なタグの付け方：
                        </h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <span>
                              <strong>技術カテゴリー：</strong> AWS、Azure、Python、Kubernetesなど
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <span>
                              <strong>学習テーマ：</strong>{' '}
                              ネットワーク、セキュリティ、データベースなど
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <span>
                              <strong>コース名：</strong> コース専用のタグ（例: aws-beginner-2024）
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div className="rounded bg-green-100 p-3 text-sm text-green-900">
                        <strong>ベストプラクティス：</strong>{' '}
                        1つのレッスンに3〜5個のタグを設定すると管理しやすくなります。
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <Separator />

              {/* 動画アップロード */}
              <section id="video-upload" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <Upload className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">動画アップロード</h2>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">アップロード手順</h3>
                    <ol className="space-y-3 text-muted-foreground">
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          1
                        </span>
                        <span>レッスン編集ページの「動画アップロード」セクションへ移動</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          2
                        </span>
                        <div>
                          <p className="font-medium">動画ファイルを選択</p>
                          <ul className="ml-4 mt-1 space-y-1 text-sm text-muted-foreground">
                            <li>・ 「ファイルを選択」ボタンをクリック、または</li>
                            <li>・ ファイルをドラッグ&ドロップ</li>
                          </ul>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          3
                        </span>
                        <span>アップロードの完了を待つ（進捗バーで確認可能）</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                          4
                        </span>
                        <span>「保存」ボタンをクリックして確定</span>
                      </li>
                    </ol>
                  </div>

                  <div className="rounded border-l-4 border-yellow-400 bg-yellow-50 p-4">
                    <h4 className="mb-2 font-semibold text-yellow-900">動画ファイルの要件</h4>
                    <ul className="space-y-1 text-sm text-yellow-900">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                        <span>
                          <strong>対応形式：</strong> MP4, WebM, OGG（推奨: MP4）
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                        <span>
                          <strong>ファイルサイズ：</strong>{' '}
                          大きなファイルはアップロードに時間がかかります
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                        <span>
                          <strong>解像度：</strong> 720p以上を推奨（1080pが最適）
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-lg border border-teal-100 bg-teal-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">
                      外部研修リンクの設定
                    </h3>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-600">
                          1
                        </span>
                        <span>レッスンタイプで「外部研修」を選択</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-600">
                          2
                        </span>
                        <span>「外部URL」フィールドに完全なURLを入力（https://...）</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-600">
                          3
                        </span>
                        <span>保存後、受講者はボタンクリックで外部サイトへ遷移</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </section>

              <Separator />

              {/* レッスン分析機能 */}
              <section id="analytics" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <BarChart3 className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">レッスン分析機能</h2>
                </div>

                <div className="space-y-6">
                  <p className="text-muted-foreground">
                    レッスン分析ダッシュボードでは、受講者の学習状況を詳細に把握し、教材の改善に役立てることができます。
                  </p>

                  <div className="rounded-lg border border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50 p-6">
                    <h3 className="mb-4 text-lg font-semibold text-foreground">主要な分析指標</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="rounded-lg bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                          <Eye className="h-5 w-5 text-blue-600" />
                          <h4 className="font-semibold text-foreground">視聴統計</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <div>
                              <strong>総視聴回数：</strong> レッスンが再生された総回数
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                            <div>
                              <strong>ユニーク視聴者数：</strong> 何人が視聴したか
                            </div>
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-lg bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                          <Clock className="h-5 w-5 text-green-600" />
                          <h4 className="font-semibold text-foreground">視聴時間</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <div>
                              <strong>総視聴時間：</strong> すべての視聴時間の合計
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <div>
                              <strong>平均視聴時間：</strong> 1人あたりの平均視聴時間
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <div>
                              <strong>平均視聴率：</strong> 動画のどれくらいまで視聴されたか（%）
                            </div>
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-lg bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-purple-600" />
                          <h4 className="font-semibold text-foreground">完了統計</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <div>
                              <strong>完了者数：</strong> レッスンを最後まで視聴した人数
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                            <div>
                              <strong>完了率：</strong> 視聴者のうち完了した割合（%）
                            </div>
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-lg bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                          <Star className="h-5 w-5 text-yellow-600" />
                          <h4 className="font-semibold text-foreground">評価・フィードバック</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                            <div>
                              <strong>平均評価：</strong> 5段階評価の平均値
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                            <div>
                              <strong>評価分布：</strong> 各評価の件数
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                            <div>
                              <strong>コメント：</strong> 受講者からの具体的なフィードバック
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-cyan-100 bg-cyan-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">
                      グラフとトレンド分析
                    </h3>
                    <div className="space-y-4">
                      <div className="rounded border bg-white p-4">
                        <h4 className="mb-2 text-sm font-semibold text-foreground">
                          日次トレンドグラフ
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          時系列で視聴数やユニークユーザー数の推移を確認できます。どの時期に視聴が集中しているか、トレンドを把握できます。
                        </p>
                      </div>
                      <div className="rounded border bg-white p-4">
                        <h4 className="mb-2 text-sm font-semibold text-foreground">
                          離脱ポイント分析
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          動画のどの時点で受講者が視聴をやめたか（離脱率）を可視化。改善が必要な箇所を特定できます。
                        </p>
                      </div>
                      <div className="rounded border bg-white p-4">
                        <h4 className="mb-2 text-sm font-semibold text-foreground">
                          評価分布グラフ
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          5段階評価の分布を円グラフで表示。全体的な満足度を一目で確認できます。
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded border-l-4 border-green-400 bg-green-50 p-4">
                    <h4 className="mb-2 font-semibold text-green-900">分析データの活用方法</h4>
                    <ul className="space-y-2 text-sm text-green-900">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                        <span>
                          <strong>完了率が低い場合：</strong>{' '}
                          動画が長すぎる、内容が難しすぎる可能性があります
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                        <span>
                          <strong>離脱ポイントが高い箇所：</strong>{' '}
                          その部分の説明を改善するか、チャプターを分割
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                        <span>
                          <strong>評価が低い場合：</strong>{' '}
                          フィードバックコメントを読んで具体的な改善点を把握
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <Separator />

              {/* 受講者管理 */}
              <section id="student-management" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <Users className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">受講者管理</h2>
                </div>

                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    「受講者管理」ページでは、コースに登録している受講者の一覧と個別の学習進捗を確認できます。
                  </p>

                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">受講者一覧</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                        <span>各コースの受講者リストを表示</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                        <span>受講者名、登録日、進捗状況を一覧で確認</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                        <span>個別の受講者をクリックして詳細な学習履歴を表示</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <Separator />

              {/* 質問・フィードバック管理 */}
              <section id="qa-management" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <MessageSquare className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">質問・フィードバック管理</h2>
                </div>

                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    レッスンページで受講者からの質問に回答したり、フィードバックを確認できます。
                  </p>

                  <div className="rounded-lg border border-purple-100 bg-purple-50 p-6">
                    <h3 className="mb-3 text-lg font-semibold text-foreground">質問への回答</h3>
                    <ol className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                          1
                        </span>
                        <span>レッスンページの右側パネルで質問一覧を確認</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                          2
                        </span>
                        <span>質問をクリックして詳細と既存の回答を表示</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                          3
                        </span>
                        <span>回答を入力して投稿（講師バッジ付きで表示されます）</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-600">
                          4
                        </span>
                        <span>重要な回答は「採用済み」としてマーク可能</span>
                      </li>
                    </ol>
                  </div>

                  <div className="rounded border-l-4 border-amber-400 bg-amber-50 p-4">
                    <p className="text-sm text-amber-900">
                      <strong>ヒント：</strong>{' '}
                      タイムスタンプ付きの質問は、動画の特定箇所に関する質問です。該当部分を確認してから回答すると効果的です。
                    </p>
                  </div>
                </div>
              </section>

              <Separator />

              {/* ベストプラクティス */}
              <section id="best-practices" className="scroll-mt-6">
                <div className="mb-4 flex items-center gap-3">
                  <Star className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-2xl font-bold text-foreground">ベストプラクティス</h2>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                        <Video className="h-5 w-5 text-blue-600" />
                        動画コンテンツ
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>・ 1レッスン5〜15分程度に抑える</li>
                        <li>・ 明確な学習目標を設定</li>
                        <li>・ 字幕やスライドで理解を補助</li>
                        <li>・ 高品質な音声で録画</li>
                      </ul>
                    </div>

                    <div className="rounded-lg border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                        コース構成
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>・ 体系的な学習順序を設計</li>
                        <li>・ 初級から上級へ段階的に</li>
                        <li>・ 関連レッスンをタグでグループ化</li>
                        <li>・ コース説明で前提知識を明記</li>
                      </ul>
                    </div>

                    <div className="rounded-lg border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                        <MessageSquare className="h-5 w-5 text-green-600" />
                        受講者対応
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>・ 質問には24時間以内に回答</li>
                        <li>・ 丁寧で具体的な説明を心がける</li>
                        <li>・ よくある質問はFAQとして整理</li>
                        <li>・ フィードバックを教材改善に活用</li>
                      </ul>
                    </div>

                    <div className="rounded-lg border border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50 p-5">
                      <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
                        <BarChart3 className="h-5 w-5 text-amber-600" />
                        継続的改善
                      </h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>・ 定期的に分析データを確認</li>
                        <li>・ 離脱率が高い箇所を改善</li>
                        <li>・ 評価の低いレッスンを見直し</li>
                        <li>・ 新しい技術動向を反映</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <TrainingManualContent />

              {/* Footer */}
              <div className="mt-12 border-t pt-6">
                <p className="text-center text-sm text-muted-foreground">
                  最終更新日: 2026-09-29 | バージョン: 2.0
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
